import { Injectable } from '@angular/core';
import * as RecordRTC from 'recordrtc';
import { Observable, Subject } from 'rxjs';
import { RecordedAudioOutput } from '../models/recorded-prompt';

@Injectable({ providedIn: 'root' })
export class AudioRecordingService {
   private stream: MediaStream | null;

   private recorder;

   private interval;

   private startTime = 0;

   private _recorded = new Subject<RecordedAudioOutput | null>();

   private _recordingTime = new Subject<string | undefined>();

   private _recordingFailed = new Subject<string | undefined>();

   getRecordedBlob(): Observable<RecordedAudioOutput | null> {
      return this._recorded.asObservable();
   }

   clearRecordedBlob() {
      this._recorded.next(null);
   }

   getRecordedTime(): Observable<string | undefined> {
      return this._recordingTime.asObservable();
   }

   recordingFailed(): Observable<string | undefined> {
      return this._recordingFailed.asObservable();
   }

   startRecording() {
      if (this.recorder) {
         // It means recording is already started or it is already recording something
         return;
      }

      this._recordingTime.next('00:00');
      navigator.mediaDevices
         .getUserMedia({ audio: true })
         .then((s) => {
            this.stream = s;
            this.record();
         })
         .catch((error) => {
            this._recordingFailed.next(undefined);
         });
   }

   stopRecording(mp3Name) {
      if (this.recorder) {
         this.recorder.stop(
            (blob) => {
               if (this.startTime) {
                  const mp3NameEncoded = encodeURIComponent(mp3Name);
                  this.stopMedia();
                  this._recorded.next({ blob: blob, title: mp3NameEncoded });
               }
            },
            () => {
               this.stopMedia();
               this._recordingFailed.next(undefined);
            }
         );
      }
   }

   abortRecording() {
      this.stopMedia();
   }

   private record() {
      this.recorder = new RecordRTC.StereoAudioRecorder(this.stream, {
         mimeType: 'audio/wav',
         type: 'audio',
      });

      this.recorder.record();
      this.startTime = Date.now();

      this.interval = setInterval(() => {
         const diffMs = Date.now() - this.startTime;
         const totalSeconds = Math.floor(diffMs / 1000);
         const minutes = Math.floor(totalSeconds / 60);
         const seconds = totalSeconds % 60;

         const time = `${this.pad(minutes)}:${this.pad(seconds)}`;
         this._recordingTime.next(time);
      }, 1000);
   }

   private pad(value: number): string {
      return value.toString().padStart(2, '0');
   }

   private stopMedia() {
      if (this.recorder) {
         this.recorder = null;
         clearInterval(this.interval);
         this.startTime = 0;
         if (this.stream) {
            this.stream.getAudioTracks().forEach((track) => {
               return track.stop();
            });
            this.stream = null;
         }
      }
   }
}
